"use client";

import { c as _c } from "react/compiler-runtime";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropId } from "@/generated/models/ApiDropId";
import type { Drop } from "@/helpers/waves/drop.helpers";
import useCapacitor from "@/hooks/useCapacitor";
import useDmWavesList from "@/hooks/useDmWavesList";
import useWavesList from "@/hooks/useWavesList";
import { WebSocketStatus } from "@/services/websocket/WebSocketTypes";
import { useWebsocketStatus } from "@/services/websocket/useWebSocketMessage";
import type { ReactNode } from "react";
import React, { createContext, useCallback, useContext, useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { WaveMessages } from "./hooks/types";
import { useActiveWaveManager } from "./hooks/useActiveWaveManager";
import type { MinimalWave } from "./hooks/useEnhancedWavesListCore";
import useEnhancedWavesListCore from "./hooks/useEnhancedWavesListCore";
import { useWaveDataManager } from "./hooks/useWaveDataManager";
import type { Listener as WaveMessagesListener } from "./hooks/useWaveMessagesStore";
import useWaveMessagesStore from "./hooks/useWaveMessagesStore";
import type { NextPageProps } from "./hooks/useWavePagination";
import type { ProcessIncomingDropType } from "./hooks/useWaveRealtimeUpdater";
import { useWaveRealtimeUpdater } from "./hooks/useWaveRealtimeUpdater";

// Define nested structures for context data
interface WavesContextData {
  readonly list: MinimalWave[];
  readonly isFetching: boolean;
  readonly isFetchingNextPage: boolean;
  readonly hasNextPage: boolean;
  readonly fetchNextPage: () => void;
  readonly addPinnedWave: (id: string) => void;
  readonly removePinnedWave: (id: string) => void;
  readonly restoreWaveUnreadCount: (waveId: string, count?: number) => void;
}
interface ActiveWaveSetOptions {
  readonly isDirectMessage?: boolean | undefined;
  readonly replace?: boolean | undefined;
  readonly serialNo?: number | string | null | undefined;
  readonly divider?: number | null | undefined;
}
interface ActiveWaveContextData {
  readonly id: string | null;
  readonly set: (waveId: string | null, options?: ActiveWaveSetOptions) => void;
}

// Define the interface for the wave messages store functions
interface WaveMessagesStoreData {
  readonly getData: (key: string) => WaveMessages | undefined;
  readonly subscribe: (key: string, listener: WaveMessagesListener) => void;
  readonly unsubscribe: (key: string, listener: WaveMessagesListener) => void;
}

// Define the type for our context using nested structures
interface MyStreamContextType {
  readonly waves: WavesContextData;
  readonly directMessages: WavesContextData;
  readonly activeWave: ActiveWaveContextData;
  readonly waveMessagesStore: WaveMessagesStoreData;
  readonly registerWave: (waveId: string, syncNewest?: boolean) => void;
  readonly fetchNextPageForWave: (props: NextPageProps) => Promise<(ApiDrop | ApiDropId)[] | null>;
  readonly fetchAroundSerialNo: (waveId: string, serialNo: number) => void;
  readonly processIncomingDrop: (drop: ApiDrop, type: ProcessIncomingDropType) => void;
  readonly processDropRemoved: (waveId: string, dropId: string) => void;
  readonly applyOptimisticDropUpdate: ({
    waveId,
    dropId,
    update
  }: {
    waveId: string;
    dropId: string;
    update: (draft: Drop) => Drop | void;
  }) => {
    rollback: () => void;
  } | null;
}
interface MyStreamProviderProps {
  readonly children: ReactNode;
}
const BROWSER_RESUME_SYNC_COOLDOWN_MS = 1000;

// Create the context
const MyStreamContext = createContext<MyStreamContextType | null>(null);

// Create a provider component
export const MyStreamProvider: React.FC<MyStreamProviderProps> = t0 => {
  const $ = _c(104);
  const {
    children
  } = t0;
  const {
    isCapacitor,
    isActive
  } = useCapacitor();
  const {
    activeWaveId,
    setActiveWave
  } = useActiveWaveManager();
  const mainWavesData = useWavesList();
  const dmWavesData = useDmWavesList();
  let t1;
  if ($[0] !== mainWavesData.waves) {
    t1 = new Set(mainWavesData.waves.map(_temp));
    $[0] = mainWavesData.waves;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const mainWaveIds = t1;
  let t2;
  if ($[2] !== dmWavesData.waves) {
    t2 = new Set(dmWavesData.waves.map(_temp2));
    $[2] = dmWavesData.waves;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const dmWaveIds = t2;
  let t3;
  if ($[4] !== dmWaveIds) {
    t3 = {
      supportsPinning: true,
      otherListWaveIds: dmWaveIds
    };
    $[4] = dmWaveIds;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const wavesHookData = useEnhancedWavesListCore(activeWaveId, mainWavesData, t3);
  let t4;
  if ($[6] !== mainWaveIds) {
    t4 = {
      supportsPinning: false,
      otherListWaveIds: mainWaveIds
    };
    $[6] = mainWaveIds;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const dmWavesHookData = useEnhancedWavesListCore(activeWaveId, dmWavesData, t4);
  const waveMessagesStore = useWaveMessagesStore();
  const websocketStatus = useWebsocketStatus();
  const prevIsActiveRef = useRef(isActive);
  const lastBrowserResumeSyncAtRef = useRef(0);
  const {
    removeWaveDeliveredNotifications
  } = useNotificationsContext();
  let t5;
  if ($[8] !== waveMessagesStore.getData || $[9] !== waveMessagesStore.removeDrop || $[10] !== waveMessagesStore.updateData) {
    t5 = {
      updateData: waveMessagesStore.updateData,
      getData: waveMessagesStore.getData,
      removeDrop: waveMessagesStore.removeDrop
    };
    $[8] = waveMessagesStore.getData;
    $[9] = waveMessagesStore.removeDrop;
    $[10] = waveMessagesStore.updateData;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const waveDataManager = useWaveDataManager(t5);
  const {
    registerWave,
    syncNewestMessages,
    fetchNextPage,
    fetchAroundSerialNo
  } = waveDataManager;
  const refetchAllMainWaves = wavesHookData.refetchAllWaves;
  const refetchAllDmWaves = dmWavesHookData.refetchAllWaves;
  const resetAllMainWavesNewDropsCount = wavesHookData.resetAllWavesNewDropsCount;
  const resetAllDmWavesNewDropsCount = dmWavesHookData.resetAllWavesNewDropsCount;
  const wavesRef = useRef(wavesHookData.waves);
  const dmWavesRef = useRef(dmWavesHookData.waves);
  let t6;
  let t7;
  if ($[12] !== dmWavesHookData.waves || $[13] !== wavesHookData.waves) {
    t6 = () => {
      wavesRef.current = wavesHookData.waves;
      dmWavesRef.current = dmWavesHookData.waves;
    };
    t7 = [wavesHookData.waves, dmWavesHookData.waves];
    $[12] = dmWavesHookData.waves;
    $[13] = wavesHookData.waves;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useLayoutEffect(t6, t7);
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = waveId => {
      const wave_1 = wavesRef.current.find(w => w.id === waveId);
      if (wave_1) {
        return wave_1.isMuted;
      }
      const dmWave = dmWavesRef.current.find(w_0 => w_0.id === waveId);
      if (dmWave) {
        return dmWave.isMuted;
      }
      return false;
    };
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const isWaveMuted = t8;
  let t9;
  if ($[17] !== activeWaveId || $[18] !== registerWave || $[19] !== removeWaveDeliveredNotifications || $[20] !== syncNewestMessages || $[21] !== waveMessagesStore.getData || $[22] !== waveMessagesStore.removeDrop || $[23] !== waveMessagesStore.updateData) {
    t9 = {
      activeWaveId,
      getData: waveMessagesStore.getData,
      updateData: waveMessagesStore.updateData,
      registerWave,
      syncNewestMessages,
      removeDrop: waveMessagesStore.removeDrop,
      removeWaveDeliveredNotifications,
      isWaveMuted
    };
    $[17] = activeWaveId;
    $[18] = registerWave;
    $[19] = removeWaveDeliveredNotifications;
    $[20] = syncNewestMessages;
    $[21] = waveMessagesStore.getData;
    $[22] = waveMessagesStore.removeDrop;
    $[23] = waveMessagesStore.updateData;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const {
    processIncomingDrop,
    processDropRemoved
  } = useWaveRealtimeUpdater(t9);
  let t10;
  if ($[25] !== registerWave || $[26] !== setActiveWave) {
    t10 = (waveId_0, options) => {
      if (waveId_0) {
        registerWave(waveId_0, true);
      }
      setActiveWave(waveId_0, options);
    };
    $[25] = registerWave;
    $[26] = setActiveWave;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const setActiveWaveAndRegister = t10;
  let t11;
  if ($[28] !== activeWaveId || $[29] !== refetchAllDmWaves || $[30] !== refetchAllMainWaves || $[31] !== registerWave) {
    t11 = () => {
      if (activeWaveId) {
        registerWave(activeWaveId, true);
      }
      refetchAllMainWaves();
      refetchAllDmWaves();
    };
    $[28] = activeWaveId;
    $[29] = refetchAllDmWaves;
    $[30] = refetchAllMainWaves;
    $[31] = registerWave;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const syncActiveWaveAndRefetch = useEffectEvent(t11);
  let t12;
  if ($[33] !== syncActiveWaveAndRefetch) {
    t12 = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      const now = Date.now();
      const sinceLastBrowserResumeSyncMs = now - lastBrowserResumeSyncAtRef.current;
      if (sinceLastBrowserResumeSyncMs < BROWSER_RESUME_SYNC_COOLDOWN_MS) {
        return;
      }
      lastBrowserResumeSyncAtRef.current = now;
      syncActiveWaveAndRefetch();
    };
    $[33] = syncActiveWaveAndRefetch;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const runBrowserResumeSync = useEffectEvent(t12);
  let t13;
  if ($[35] !== isCapacitor || $[36] !== resetAllDmWavesNewDropsCount || $[37] !== resetAllMainWavesNewDropsCount || $[38] !== syncActiveWaveAndRefetch) {
    t13 = () => {
      syncActiveWaveAndRefetch();
      if (isCapacitor) {
        resetAllMainWavesNewDropsCount();
        resetAllDmWavesNewDropsCount();
      }
    };
    $[35] = isCapacitor;
    $[36] = resetAllDmWavesNewDropsCount;
    $[37] = resetAllMainWavesNewDropsCount;
    $[38] = syncActiveWaveAndRefetch;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  const handleConnectedWebSocket = useEffectEvent(t13);
  let t14;
  if ($[40] !== resetAllDmWavesNewDropsCount || $[41] !== resetAllMainWavesNewDropsCount || $[42] !== syncActiveWaveAndRefetch) {
    t14 = () => {
      syncActiveWaveAndRefetch();
      resetAllMainWavesNewDropsCount();
      resetAllDmWavesNewDropsCount();
    };
    $[40] = resetAllDmWavesNewDropsCount;
    $[41] = resetAllMainWavesNewDropsCount;
    $[42] = syncActiveWaveAndRefetch;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const handleCapacitorResume = useEffectEvent(t14);
  let t15;
  if ($[44] !== handleConnectedWebSocket || $[45] !== websocketStatus) {
    t15 = () => {
      if (websocketStatus !== WebSocketStatus.CONNECTED) {
        return;
      }
      handleConnectedWebSocket();
    };
    $[44] = handleConnectedWebSocket;
    $[45] = websocketStatus;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== websocketStatus) {
    t16 = [websocketStatus];
    $[47] = websocketStatus;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[49] !== activeWaveId || $[50] !== registerWave) {
    t17 = () => {
      if (activeWaveId) {
        registerWave(activeWaveId, true);
      }
    };
    t18 = [activeWaveId, registerWave];
    $[49] = activeWaveId;
    $[50] = registerWave;
    $[51] = t17;
    $[52] = t18;
  } else {
    t17 = $[51];
    t18 = $[52];
  }
  useEffect(t17, t18);
  let t19;
  if ($[53] !== handleCapacitorResume || $[54] !== isActive || $[55] !== isCapacitor) {
    t19 = () => {
      if (!isCapacitor) {
        return;
      }
      if (!prevIsActiveRef.current && isActive) {
        handleCapacitorResume();
      }
      prevIsActiveRef.current = isActive;
    };
    $[53] = handleCapacitorResume;
    $[54] = isActive;
    $[55] = isCapacitor;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== isActive || $[58] !== isCapacitor) {
    t20 = [isActive, isCapacitor];
    $[57] = isActive;
    $[58] = isCapacitor;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  useEffect(t19, t20);
  let t21;
  if ($[60] !== isCapacitor || $[61] !== runBrowserResumeSync) {
    t21 = () => {
      if (isCapacitor) {
        return;
      }
      const handleVisibilityChange = () => {
        if (document.visibilityState !== "visible") {
          return;
        }
        runBrowserResumeSync();
      };
      const handleFocus = () => {
        runBrowserResumeSync();
      };
      const handleOnline = () => {
        runBrowserResumeSync();
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("focus", handleFocus);
      window.addEventListener("online", handleOnline);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("focus", handleFocus);
        window.removeEventListener("online", handleOnline);
      };
    };
    $[60] = isCapacitor;
    $[61] = runBrowserResumeSync;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== isCapacitor) {
    t22 = [isCapacitor];
    $[63] = isCapacitor;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  useEffect(t21, t22);
  let t23;
  if ($[65] !== wavesHookData.addPinnedWave || $[66] !== wavesHookData.fetchNextPage || $[67] !== wavesHookData.hasNextPage || $[68] !== wavesHookData.isFetching || $[69] !== wavesHookData.isFetchingNextPage || $[70] !== wavesHookData.removePinnedWave || $[71] !== wavesHookData.restoreWaveUnreadCount || $[72] !== wavesHookData.waves) {
    t23 = {
      list: wavesHookData.waves,
      isFetching: wavesHookData.isFetching,
      isFetchingNextPage: wavesHookData.isFetchingNextPage,
      hasNextPage: wavesHookData.hasNextPage,
      fetchNextPage: wavesHookData.fetchNextPage,
      addPinnedWave: wavesHookData.addPinnedWave,
      removePinnedWave: wavesHookData.removePinnedWave,
      restoreWaveUnreadCount: wavesHookData.restoreWaveUnreadCount
    };
    $[65] = wavesHookData.addPinnedWave;
    $[66] = wavesHookData.fetchNextPage;
    $[67] = wavesHookData.hasNextPage;
    $[68] = wavesHookData.isFetching;
    $[69] = wavesHookData.isFetchingNextPage;
    $[70] = wavesHookData.removePinnedWave;
    $[71] = wavesHookData.restoreWaveUnreadCount;
    $[72] = wavesHookData.waves;
    $[73] = t23;
  } else {
    t23 = $[73];
  }
  const waves = t23;
  let t24;
  if ($[74] !== dmWavesHookData.addPinnedWave || $[75] !== dmWavesHookData.fetchNextPage || $[76] !== dmWavesHookData.hasNextPage || $[77] !== dmWavesHookData.isFetching || $[78] !== dmWavesHookData.isFetchingNextPage || $[79] !== dmWavesHookData.removePinnedWave || $[80] !== dmWavesHookData.restoreWaveUnreadCount || $[81] !== dmWavesHookData.waves) {
    t24 = {
      list: dmWavesHookData.waves,
      isFetching: dmWavesHookData.isFetching,
      isFetchingNextPage: dmWavesHookData.isFetchingNextPage,
      hasNextPage: dmWavesHookData.hasNextPage,
      fetchNextPage: dmWavesHookData.fetchNextPage,
      addPinnedWave: dmWavesHookData.addPinnedWave,
      removePinnedWave: dmWavesHookData.removePinnedWave,
      restoreWaveUnreadCount: dmWavesHookData.restoreWaveUnreadCount
    };
    $[74] = dmWavesHookData.addPinnedWave;
    $[75] = dmWavesHookData.fetchNextPage;
    $[76] = dmWavesHookData.hasNextPage;
    $[77] = dmWavesHookData.isFetching;
    $[78] = dmWavesHookData.isFetchingNextPage;
    $[79] = dmWavesHookData.removePinnedWave;
    $[80] = dmWavesHookData.restoreWaveUnreadCount;
    $[81] = dmWavesHookData.waves;
    $[82] = t24;
  } else {
    t24 = $[82];
  }
  const directMessages = t24;
  let t25;
  if ($[83] !== activeWaveId || $[84] !== setActiveWaveAndRegister) {
    t25 = {
      id: activeWaveId,
      set: setActiveWaveAndRegister
    };
    $[83] = activeWaveId;
    $[84] = setActiveWaveAndRegister;
    $[85] = t25;
  } else {
    t25 = $[85];
  }
  const activeWave = t25;
  let t26;
  if ($[86] !== waveMessagesStore.getData || $[87] !== waveMessagesStore.subscribe || $[88] !== waveMessagesStore.unsubscribe) {
    t26 = {
      getData: waveMessagesStore.getData,
      subscribe: waveMessagesStore.subscribe,
      unsubscribe: waveMessagesStore.unsubscribe
    };
    $[86] = waveMessagesStore.getData;
    $[87] = waveMessagesStore.subscribe;
    $[88] = waveMessagesStore.unsubscribe;
    $[89] = t26;
  } else {
    t26 = $[89];
  }
  const waveMessagesStoreData = t26;
  let t27;
  if ($[90] !== activeWave || $[91] !== directMessages || $[92] !== fetchAroundSerialNo || $[93] !== fetchNextPage || $[94] !== processDropRemoved || $[95] !== processIncomingDrop || $[96] !== registerWave || $[97] !== waveMessagesStore.optimisticUpdateDrop || $[98] !== waveMessagesStoreData || $[99] !== waves) {
    t27 = {
      waves,
      directMessages,
      activeWave,
      waveMessagesStore: waveMessagesStoreData,
      registerWave,
      fetchNextPageForWave: fetchNextPage,
      fetchAroundSerialNo,
      processIncomingDrop,
      processDropRemoved,
      applyOptimisticDropUpdate: waveMessagesStore.optimisticUpdateDrop
    };
    $[90] = activeWave;
    $[91] = directMessages;
    $[92] = fetchAroundSerialNo;
    $[93] = fetchNextPage;
    $[94] = processDropRemoved;
    $[95] = processIncomingDrop;
    $[96] = registerWave;
    $[97] = waveMessagesStore.optimisticUpdateDrop;
    $[98] = waveMessagesStoreData;
    $[99] = waves;
    $[100] = t27;
  } else {
    t27 = $[100];
  }
  const contextValue = t27;
  let t28;
  if ($[101] !== children || $[102] !== contextValue) {
    t28 = <MyStreamContext.Provider value={contextValue}>{children}</MyStreamContext.Provider>;
    $[101] = children;
    $[102] = contextValue;
    $[103] = t28;
  } else {
    t28 = $[103];
  }
  return t28;
};

// Create a custom hook for using this context
export const useMyStream = () => {
  const context = useContext(MyStreamContext);
  if (!context) {
    throw new Error("useMyStream must be used within a MyStreamProvider");
  }
  return context;
};
export const useMyStreamOptional = () => {
  return useContext(MyStreamContext);
};

// Create the selector hook for wave messages
export function useMyStreamWaveMessages(waveId) {
  const $ = _c(12);
  const {
    waveMessagesStore
  } = useMyStream();
  const {
    getData,
    subscribe,
    unsubscribe
  } = waveMessagesStore;
  let t0;
  if ($[0] !== getData || $[1] !== waveId) {
    t0 = () => waveId ? getData(waveId) : undefined;
    $[0] = getData;
    $[1] = waveId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [data, setData] = useState(t0);
  let t1;
  let t2;
  if ($[3] !== subscribe || $[4] !== unsubscribe || $[5] !== waveId) {
    t1 = () => {
      if (!waveId) {
        setData(undefined);
        return;
      }
      const listener = newData => {
        setData(currentData => {
          if (JSON.stringify(currentData) !== JSON.stringify(newData)) {
            return newData;
          }
          return currentData;
        });
      };
      subscribe(waveId, listener);
      return () => {
        unsubscribe(waveId, listener);
      };
    };
    t2 = [waveId, subscribe, unsubscribe];
    $[3] = subscribe;
    $[4] = unsubscribe;
    $[5] = waveId;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[8] !== getData || $[9] !== waveId) {
    t3 = () => {
      setData(waveId ? getData(waveId) : undefined);
    };
    t4 = [waveId, getData];
    $[8] = getData;
    $[9] = waveId;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  return data;
}
function _temp(wave) {
  return wave.id;
}
function _temp2(wave_0) {
  return wave_0.id;
}